import { useEffect, useMemo, useState } from "react";
import { TopNav } from "../components/TopNav";
import { Footer } from "../components/Footer";
import styles from "./NotesPage.module.css";

/**
 * Notes — per-app marketing page. Tonal companion to Stickies but
 * visually distinct: teal/cream palette, ruled-paper motif in the
 * hero, and a substantial animated demo of the actual app's
 * sidebar/list/editor shape running on a loop.
 *
 * The demo is driven by a step machine in {@link NotesDemo}. No
 * canvas, no live bundle — just React state advancing a script of
 * scenes (hover → click → tile-enter → type → switch project →
 * open note → type body), with cursor positioning interpolated by
 * CSS transitions.
 */
export function NotesPage() {
  return (
    <>
      <TopNav />
      <main>
        <Hero />
        <DemoSection />
        <ProjectShowcase />
        <Features />
        <Cta />
      </main>
      <Footer />
    </>
  );
}

/* ============================================================ */
/* HERO                                                          */
/* ============================================================ */

function Hero() {
  return (
    <section className={styles.hero} aria-label="Notes">
      <div className={styles.heroPaper} aria-hidden />
      <div className={styles.heroMargin} aria-hidden />
      <div className={styles.heroGrain} aria-hidden />

      <div className={`atlasContainer ${styles.heroInner}`}>
        <div className={styles.heroCopy}>
          <span className={styles.heroEyebrow}>ATLAS</span>
          <h1 className={styles.heroTitle}>
            NOTES<span className={styles.heroTitleCaret} aria-hidden />
          </h1>
          <p className={styles.heroLede}>
            A quiet place for thoughts that need more than a sticky.
            Projects, checklists, rich text — without the instruction manual.
          </p>
          <div className={styles.heroActions}>
            <a className={styles.heroPrimary} href="/#download">
              Download <span aria-hidden>→</span>
            </a>
            <a
              className={styles.heroSecondary}
              href="/preview/"
              target="_blank"
              rel="noopener"
              title="Open the live Atlas Desk preview in a new tab"
            >
              <span className={styles.heroSecondaryDot} aria-hidden />
              Try in browser
            </a>
          </div>
        </div>

        {/* Single tilted notebook page with ruled lines and four lines
            of "handwriting" that type themselves in sequence. The
            font-family falls back through Caveat → script faces → cursive
            so it reads as handwritten on every platform. */}
        <article className={styles.heroPaperSheet} aria-hidden>
          <h2 className={styles.heroPaperHeading}>Sunday journal</h2>
          <div className={styles.heroPaperLines}>
            <span className={`${styles.heroPaperLine} ${styles.heroPaperLine1}`}>
              Walked the long way home.
            </span>
            <span className={`${styles.heroPaperLine} ${styles.heroPaperLine2}`}>
              Sat by the river for an hour.
            </span>
            <span className={`${styles.heroPaperLine} ${styles.heroPaperLine3}`}>
              No phone. No tabs. Just quiet.
            </span>
            <span className={`${styles.heroPaperLine} ${styles.heroPaperLine4}`}>
              Tomorrow: pick up where I left off.
            </span>
          </div>
        </article>
      </div>
    </section>
  );
}

/* ============================================================ */
/* DEMO SECTION — the animated walkthrough                       */
/* ============================================================ */

/**
 * The demo script: a list of scenes, each with a duration. The
 * driver advances one at a time, looping. Each scene declares:
 *   - what the cursor should be pointing at
 *   - which project is active
 *   - which note is open (or null for list)
 *   - whether the "+ New" button is pressed
 *   - whether a tile is in its "entering" state
 *   - whether the title/body should be typing (in which case the
 *     visible string is sliced from `targetTitle`/`targetBody`)
 *
 * The driver runs a master timer plus a typing sub-timer for the
 * char-by-char effect.
 */
type Scene =
  | { kind: "rest-all"; duration: number }
  | { kind: "hover-new"; duration: number }
  | { kind: "click-new"; duration: number }
  | { kind: "move-to-tile"; duration: number }
  | { kind: "type-title"; duration: number }
  | { kind: "rest-after-create"; duration: number }
  | { kind: "hover-fresh-tile"; duration: number }
  | { kind: "pickup-fresh"; duration: number }
  | { kind: "drag-to-reading"; duration: number }
  | { kind: "drop-on-reading"; duration: number }
  | { kind: "rest-after-drop"; duration: number }
  | { kind: "hover-project"; project: string; duration: number }
  | { kind: "click-project"; project: string; duration: number }
  | { kind: "rest-project"; project: string; duration: number }
  | { kind: "hover-tile"; tileId: string; duration: number }
  | { kind: "click-tile"; tileId: string; duration: number }
  | { kind: "type-body"; tileId: string; duration: number }
  | { kind: "rest-after-typing"; tileId: string; duration: number }
  | { kind: "hover-select-start"; tileId: string; duration: number }
  | { kind: "select-phrase"; tileId: string; duration: number }
  | { kind: "rest-after-select"; tileId: string; duration: number }
  | { kind: "hover-bold"; tileId: string; duration: number }
  | { kind: "click-bold"; tileId: string; duration: number }
  | { kind: "rest-after-bold"; tileId: string; duration: number }
  | { kind: "hover-back"; duration: number }
  | { kind: "click-back"; duration: number }
  | { kind: "rest-after-back"; duration: number }
  | { kind: "hover-all"; duration: number }
  | { kind: "click-all"; duration: number }
  | { kind: "rest-all-final"; duration: number };

const NEW_TITLE = "Sunday letters";

// Body composed of three pieces so the demo can wrap the middle
// phrase in a selection highlight and then a <strong> for the bold
// scene. NEW_BODY is the joined string used during char-by-char
// typing in the type-body scene.
const BODY_PRE = "Software ";
const BODY_PHRASE = "shouldn't ask for permission";
const BODY_POST = " to be quiet.\nIt should just be quiet.";
const NEW_BODY = BODY_PRE + BODY_PHRASE + BODY_POST;

const SCENES: Scene[] = [
  // Phase 1 — create a fresh note in Inbox.
  { kind: "rest-all", duration: 2000 },
  { kind: "hover-new", duration: 700 },
  { kind: "click-new", duration: 400 },
  // Cursor flies from the "+ New" button down to the freshly-created
  // tile before typing starts. The empty tile is already visible by
  // this scene — only the cursor moves.
  { kind: "move-to-tile", duration: 700 },
  { kind: "type-title", duration: 2200 },
  { kind: "rest-after-create", duration: 900 },
  // Phase 2 — drag the fresh tile from All notes into the Reading project.
  { kind: "hover-fresh-tile", duration: 600 },
  { kind: "pickup-fresh", duration: 400 },
  { kind: "drag-to-reading", duration: 1100 },
  { kind: "drop-on-reading", duration: 500 },
  { kind: "rest-after-drop", duration: 1000 },
  // Phase 3 — navigate into Reading, open a note, type a body.
  { kind: "hover-project", project: "reading", duration: 600 },
  { kind: "click-project", project: "reading", duration: 400 },
  { kind: "rest-project", project: "reading", duration: 1000 },
  { kind: "hover-tile", tileId: "r1", duration: 600 },
  { kind: "click-tile", tileId: "r1", duration: 400 },
  { kind: "type-body", tileId: "r1", duration: 3000 },
  // Phase 4 — select a phrase and apply bold. The cursor lands at
  // the phrase START first (text-cursor mode), then drags to the
  // phrase END while the highlight grows in sync — mimicking a real
  // click-drag text selection.
  { kind: "rest-after-typing", tileId: "r1", duration: 700 },
  { kind: "hover-select-start", tileId: "r1", duration: 500 },
  { kind: "select-phrase", tileId: "r1", duration: 800 },
  { kind: "rest-after-select", tileId: "r1", duration: 400 },
  { kind: "hover-bold", tileId: "r1", duration: 500 },
  { kind: "click-bold", tileId: "r1", duration: 400 },
  { kind: "rest-after-bold", tileId: "r1", duration: 1400 },
  // Phase 5 — navigate back to All notes for a clean loop seam.
  { kind: "hover-back", duration: 600 },
  { kind: "click-back", duration: 400 },
  { kind: "rest-after-back", duration: 900 },
  { kind: "hover-all", duration: 600 },
  { kind: "click-all", duration: 400 },
  { kind: "rest-all-final", duration: 1800 },
];

type PatternKey = "solid" | "dots" | "lines" | "cross" | "wave" | "grid";

interface ProjectDef {
  id: string;
  name: string;
  color: string;
  pattern: PatternKey;
  count: number;
}

/**
 * Inlined copy of `atlasSwatchBackground` from packages/client/swatch.ts.
 * The website is intentionally zero-dep on the workspace, so we vendor
 * this small function rather than importing @atlas/client. Keep in sync
 * with the canonical version if the suite ever changes pattern recipes.
 */
function patternBg(baseHex: string, pattern: PatternKey, scale = 1): string {
  const ink = "rgba(23, 23, 23, 0.35)";
  const s = scale;
  switch (pattern) {
    case "solid":
      return baseHex;
    case "dots":
      return `radial-gradient(${ink} ${1.5 * s}px, transparent ${1.7 * s}px) 0 0 / ${8 * s}px ${8 * s}px, ${baseHex}`;
    case "lines":
      return `repeating-linear-gradient(135deg, ${ink} 0 ${1.5 * s}px, transparent ${1.5 * s}px ${6 * s}px), ${baseHex}`;
    case "cross":
      return `repeating-linear-gradient(45deg, ${ink} 0 ${1 * s}px, transparent ${1 * s}px ${6 * s}px), repeating-linear-gradient(-45deg, ${ink} 0 ${1 * s}px, transparent ${1 * s}px ${6 * s}px), ${baseHex}`;
    case "wave":
      return `radial-gradient(circle at ${4 * s}px 0, transparent ${4 * s}px, ${ink} ${4.5 * s}px, transparent ${5 * s}px) 0 0 / ${8 * s}px ${8 * s}px, ${baseHex}`;
    case "grid":
      return `repeating-linear-gradient(0deg, ${ink} 0 ${1 * s}px, transparent ${1 * s}px ${6 * s}px), repeating-linear-gradient(90deg, ${ink} 0 ${1 * s}px, transparent ${1 * s}px ${6 * s}px), ${baseHex}`;
  }
}

const PROJECTS: ProjectDef[] = [
  { id: "all",     name: "All notes", color: "#171717", pattern: "solid", count: 9 },
  { id: "inbox",   name: "Inbox",     color: "#ffe87a", pattern: "grid",  count: 3 },
  { id: "reading", name: "Reading",   color: "#98d8cf", pattern: "lines", count: 4 },
  { id: "studio",  name: "Studio",    color: "#c8b8e8", pattern: "cross", count: 2 },
];

const PROJECTS_BY_ID: Record<string, ProjectDef> = Object.fromEntries(
  PROJECTS.map((p) => [p.id, p]),
);

interface TileDef {
  id: string;
  title: string;
  snippet: string;
  project: string;
}

const TILES: TileDef[] = [
  { id: "n1", title: "Notes on quiet UIs", snippet: "What if the dock wasn't competing with you for attention?", project: "studio" },
  { id: "n2", title: "Sourdough recipe v3", snippet: "60% hydration, 14 hr cold ferment. Score deep, score angled.", project: "inbox" },
  { id: "r1", title: "The Long Take", snippet: "On craft, slowness, and the films that take their time arriving.", project: "reading" },
  { id: "r2", title: "A Pattern Language", snippet: "Pattern 159: light on two sides of every room. Still right.", project: "reading" },
  { id: "n3", title: "Garden planning", snippet: "Move the tomatoes south. The basil wants more morning light.", project: "inbox" },
  { id: "r3", title: "Letters to a young poet", snippet: "Re-reading the second letter. The patience of waiting for the question to ripen.", project: "reading" },
  { id: "n4", title: "Atlas 0.2 release notes", snippet: "What changed, what stayed quiet, what got smaller.", project: "studio" },
  { id: "n5", title: "Friday call agenda", snippet: "Three things, none of them urgent. Just enough to keep the thread.", project: "inbox" },
  { id: "r4", title: "On reading slowly", snippet: "I've been trying to read one chapter at a time, then stopping to walk.", project: "reading" },
];

function DemoSection() {
  return (
    <section
      className={styles.demoSection}
      aria-labelledby="notes-demo-title"
    >
      <div className={`atlasContainer ${styles.demoInner}`}>
        <header className={styles.demoHeader}>
          <span className="atlasEyebrow">A guided demo</span>
          <h2 id="notes-demo-title" className={styles.sectionTitle}>
            One sidebar.{" "}
            <span className={styles.demoTitleAccent}>
              One pane.
            </span>{" "}
            No menus to memorize.
          </h2>
          <p className={styles.sectionLede}>
            Here's the whole app, on a loop — making a new note,
            switching projects, opening one to write in. The cursor
            does the work; you just watch.
          </p>
        </header>

        <NotesDemo />
      </div>
    </section>
  );
}

function NotesDemo() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const scene = SCENES[sceneIdx]!;
  const [typed, setTyped] = useState(0);

  // Advance the master scene timer. Resets the typed counter at every
  // step so type-scenes always start from 0 chars.
  useEffect(() => {
    setTyped(0);
    const id = window.setTimeout(() => {
      setSceneIdx((i) => (i + 1) % SCENES.length);
    }, scene.duration);
    return () => window.clearTimeout(id);
  }, [sceneIdx, scene.duration]);

  // Per-frame typing tick: advances `typed` smoothly during scenes
  // that animate text. Other scenes leave it untouched.
  useEffect(() => {
    if (scene.kind !== "type-title" && scene.kind !== "type-body") return;
    const target = scene.kind === "type-title" ? NEW_TITLE.length : NEW_BODY.length;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (scene.duration - 200));
      const next = Math.round(t * target);
      setTyped(next);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [sceneIdx, scene]);

  // Derived per-scene state. Single source of truth so the render is
  // a pure function of `sceneIdx` + `typed`. Passing the index (not
  // just the scene) lets `derive` carry data forward across scenes —
  // once a tile is created or a body is typed, it persists for the
  // rest of the loop iteration instead of vanishing at the next step.
  const state = useMemo(
    () => derive(sceneIdx, scene, typed),
    [sceneIdx, scene, typed],
  );

  return (
    <div className={styles.demoStage} aria-hidden>
      {/* Titlebar — Atlas chrome: app icon + name on the left,
          traffic-light buttons pushed to the right. */}
      <div className={styles.demoTitlebar}>
        <span className={styles.demoTitlebarIcon} aria-hidden>
          <NotesAppIcon />
        </span>
        <span className={styles.demoTitlebarTitle}>Notes</span>
        <span className={styles.demoTitlebarSpacer} />
        <span className={styles.demoLights} aria-hidden>
          <span className={styles.demoLight} />
          <span className={styles.demoLight} />
          <span className={`${styles.demoLight} ${styles.demoLightClose}`} />
        </span>
      </div>

      {/* App */}
      <div className={styles.demoApp}>
        <Sidebar
          activeProject={state.activeProject}
          hoverProject={state.hoverProject}
          dropTarget={state.dropTarget}
        />
        <Pane state={state} sceneIdx={sceneIdx} />
      </div>

      {/* Drag ghost — rendered when a sticky is being carried to a
          project. Tracks cursor position with the same CSS transition. */}
      {state.dragging ? (
        <Ghost
          x={state.cursorX}
          y={state.cursorY}
          title={state.pendingTitle ?? NEW_TITLE}
          project={state.freshTileProject}
        />
      ) : null}

      {/* Cursor — absolute-positioned, drives via CSS transition. */}
      <Cursor x={state.cursorX} y={state.cursorY} clicking={state.clicking} />
    </div>
  );
}

interface DerivedState {
  activeProject: string;
  hoverProject: string | null;
  openNoteId: string | null;
  pressNew: boolean;
  pressBack: boolean;
  // Pending title for a freshly-created tile (animated typing).
  pendingTitle: string | null;
  // Body for an open note (animated typing).
  bodyText: string;
  bodyTyping: boolean;
  // Fresh tile visibility + which project it currently belongs to
  // (inbox before the drag, reading after the drop).
  showFreshTile: boolean;
  freshTileProject: "inbox" | "reading";
  freshTileFading: boolean;
  // Drag mechanics — the ghost element is rendered iff `dragging`.
  dragging: boolean;
  dropTarget: string | null;
  // Editor formatting state.
  selectionState: "animating" | "full" | null;
  boldApplied: boolean;
  boldHover: boolean;
  boldPressed: boolean;
  cursorX: number;
  cursorY: number;
  clicking: boolean;
}

// Anchor positions inside the stage as % of stage width/height.
// Stage is 16:10, sidebar ~18.5% wide, titlebar ~5%, pane header
// ~6%. Tuned by eye against those proportions.
const ANCHORS = {
  rest:     { x: 56, y: 70 },
  newBtn:   { x: 92, y: 9 },
  proj: {
    all:     { x: 11, y: 16 },
    inbox:   { x: 11, y: 28 },
    reading: { x: 11, y: 34 },
    studio:  { x: 11, y: 40 },
  } as Record<string, { x: number; y: number }>,
  tile: {
    // r1 sits at the *second* grid column when the fresh tile is in
    // Reading (which it is by the time we hover/click it), so its
    // anchor is offset right of the fresh tile.
    r1: { x: 50, y: 27 },
    r2: { x: 70, y: 27 },
  } as Record<string, { x: number; y: number }>,
  // Fresh-tile sits at the top-left of the grid (rendered first
  // when visible). Same anchor as the first tile slot.
  freshTile: { x: 30, y: 27 },
  back: { x: 21, y: 9 },
  // Bold button sits at the start of the editor toolbar, just inside
  // the pane's left padding, on the row below the pane header. With
  // the title + meta strip removed, the toolbar sits directly under
  // the header at y ≈ 15%.
  boldBtn: { x: 22, y: 15 },
  // Start of the highlight phrase in the editor body. Cursor moves
  // here first, then drags to selectEnd while the highlight grows —
  // so the cursor visibly tracks the selection instead of teleporting
  // to the end.
  selectStart: { x: 24, y: 22 },
  // End of the highlight phrase. Cursor lands here at the close of
  // the select-phrase animation. Phrase ends a little past midline.
  selectEnd: { x: 45, y: 22 },
};

// Scene-index thresholds for "this thing has happened earlier in the
// loop and should still be visible." Tied to the SCENES array order
// — if you reorder scenes, update these. Cross-reference the
// `kind` comments next to each constant.
// Scene indices for the persistent-state thresholds. If you reorder
// SCENES, update these alongside it. The named constants make the
// dependencies obvious instead of magic numbers everywhere.
const SCENE_FRESH_TILE_CREATED = 2;   // click-new — tile appears empty
const SCENE_TYPE_TITLE = 4;           // type-title — char-by-char typing
const SCENE_FRESH_TILE_IN_READING = 9; // drop-on-reading
const SCENE_BODY_TYPED = 17;          // rest-after-typing onward
const SCENE_SELECTION_START = 19;     // select-phrase (highlight grows)
const SCENE_SELECTION_END = 23;       // rest-after-bold — selection clears
const SCENE_BOLD_APPLIED = 22;        // click-bold onward (bold lands on click)
// Loop reset moment — the click-All-notes scene. Persistent demo data
// (fresh tile, typed body, bold) drops here so the All-notes view that
// loads after the click is identical to scene 0. Avoids a visible
// fade + grid-reflow at the seam: instead the view-change itself
// (Reading list → All-notes list) carries the data reset cleanly.
const SCENE_LOOP_RESET = 28;

function derive(
  sceneIdx: number,
  scene: Scene,
  typed: number,
): DerivedState {
  // Persistent flags — what's already happened in this loop iteration.
  // All upper-bounded by SCENE_LOOP_RESET so the click-All-notes scene
  // at the end loads a view identical to scene 0.
  const freshTileCreated =
    sceneIdx >= SCENE_FRESH_TILE_CREATED && sceneIdx < SCENE_LOOP_RESET;
  const freshTileInReading =
    sceneIdx >= SCENE_FRESH_TILE_IN_READING && sceneIdx < SCENE_LOOP_RESET;
  const bodyAlreadyTyped =
    sceneIdx >= SCENE_BODY_TYPED && sceneIdx < SCENE_LOOP_RESET;
  const phraseSelected =
    sceneIdx >= SCENE_SELECTION_START && sceneIdx < SCENE_SELECTION_END;
  const boldApplied =
    sceneIdx >= SCENE_BOLD_APPLIED && sceneIdx < SCENE_LOOP_RESET;
  // Fade-out is no longer needed — the data reset above is masked by
  // the natural view change from Reading list → All-notes list.
  const freshTileFading = false;

  // Defaults: All notes view, no open note, cursor at rest.
  let activeProject = "all";
  let hoverProject: string | null = null;
  let openNoteId: string | null = null;
  let pressNew = false;
  let pressBack = false;
  let dragging = false;
  let dropTarget: string | null = null;
  let boldHover = false;
  let boldPressed = false;
  let cursorX = ANCHORS.rest.x;
  let cursorY = ANCHORS.rest.y;
  let clicking = false;

  switch (scene.kind) {
    case "rest-all":
    case "rest-after-create":
    case "rest-after-drop":
    case "rest-all-final":
      // Cursor sits at rest, list view of all notes.
      break;
    case "hover-new":
      ({ x: cursorX, y: cursorY } = ANCHORS.newBtn);
      break;
    case "click-new":
      ({ x: cursorX, y: cursorY } = ANCHORS.newBtn);
      pressNew = true;
      clicking = true;
      break;
    case "move-to-tile":
      ({ x: cursorX, y: cursorY } = ANCHORS.freshTile);
      break;
    case "type-title":
      ({ x: cursorX, y: cursorY } = ANCHORS.freshTile);
      break;

    // ---- drag sequence ----
    case "hover-fresh-tile":
      ({ x: cursorX, y: cursorY } = ANCHORS.freshTile);
      break;
    case "pickup-fresh":
      ({ x: cursorX, y: cursorY } = ANCHORS.freshTile);
      dragging = true;
      clicking = true;
      break;
    case "drag-to-reading":
      ({ x: cursorX, y: cursorY } = ANCHORS.proj.reading!);
      dragging = true;
      dropTarget = "reading";
      break;
    case "drop-on-reading":
      ({ x: cursorX, y: cursorY } = ANCHORS.proj.reading!);
      dragging = true;
      dropTarget = "reading";
      clicking = true;
      break;

    // ---- project navigation ----
    case "hover-project":
    case "click-project":
    case "rest-project": {
      const anchor = ANCHORS.proj[scene.project] ?? ANCHORS.rest;
      cursorX = anchor.x;
      cursorY = anchor.y;
      hoverProject = scene.project;
      if (scene.kind === "click-project" || scene.kind === "rest-project") {
        activeProject = scene.project;
      }
      if (scene.kind === "click-project") clicking = true;
      break;
    }

    // ---- open a tile ----
    case "hover-tile":
    case "click-tile": {
      activeProject = "reading";
      const anchor = ANCHORS.tile[scene.tileId] ?? ANCHORS.rest;
      cursorX = anchor.x;
      cursorY = anchor.y;
      if (scene.kind === "click-tile") {
        clicking = true;
        openNoteId = scene.tileId;
      }
      break;
    }

    // ---- editor scenes ----
    case "type-body":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.rest);
      break;
    case "rest-after-typing":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.rest);
      break;
    case "hover-select-start":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.selectStart);
      break;
    case "select-phrase":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.selectEnd);
      break;
    case "rest-after-select":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.selectEnd);
      break;
    case "hover-bold":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.boldBtn);
      boldHover = true;
      break;
    case "click-bold":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.boldBtn);
      boldHover = true;
      boldPressed = true;
      clicking = true;
      break;
    case "rest-after-bold":
      activeProject = "reading";
      openNoteId = scene.tileId;
      ({ x: cursorX, y: cursorY } = ANCHORS.boldBtn);
      break;

    // ---- back to list, then to All notes ----
    case "hover-back":
      activeProject = "reading";
      openNoteId = "r1";
      ({ x: cursorX, y: cursorY } = ANCHORS.back);
      break;
    case "click-back":
      activeProject = "reading";
      // Keep the editor mounted through the click so the user sees
      // the back button visibly press. The view transitions to the
      // list in the next scene.
      openNoteId = "r1";
      pressBack = true;
      clicking = true;
      ({ x: cursorX, y: cursorY } = ANCHORS.back);
      break;
    case "rest-after-back":
      // Reading list view, cursor still up by the back button.
      activeProject = "reading";
      ({ x: cursorX, y: cursorY } = ANCHORS.back);
      break;
    case "hover-all":
      activeProject = "reading";
      hoverProject = "all";
      ({ x: cursorX, y: cursorY } = ANCHORS.proj.all!);
      break;
    case "click-all":
      activeProject = "all";
      hoverProject = "all";
      clicking = true;
      ({ x: cursorX, y: cursorY } = ANCHORS.proj.all!);
      break;
  }

  // Fresh tile: tile pops in at click-new with an empty title, holds
  // empty until typing starts at scene SCENE_TYPE_TITLE, types
  // char-by-char during that scene, then sits full afterward. Three
  // states: not yet created (null), created-but-untyped (""), and
  // typed (sliced or full NEW_TITLE).
  const showFreshTile = freshTileCreated;
  const pendingTitle = !freshTileCreated
    ? null
    : scene.kind === "type-title"
      ? NEW_TITLE.slice(0, typed)
      : sceneIdx < SCENE_TYPE_TITLE
        ? ""
        : NEW_TITLE;

  // Body text: live-typing in `type-body`, retained through the
  // selection / bold / back scenes, gone once the loop resets.
  const bodyText =
    scene.kind === "type-body"
      ? NEW_BODY.slice(0, typed)
      : bodyAlreadyTyped
        ? NEW_BODY
        : "";
  const bodyTyping = scene.kind === "type-body";

  // Selection state: animating during select-phrase, full thereafter
  // until cleared. `null` everywhere else.
  let selectionState: "animating" | "full" | null = null;
  if (phraseSelected) {
    selectionState = scene.kind === "select-phrase" ? "animating" : "full";
  }

  const freshTileProject = freshTileInReading ? "reading" : "inbox";

  return {
    activeProject,
    hoverProject,
    openNoteId,
    pressNew,
    pressBack,
    pendingTitle,
    bodyText,
    bodyTyping,
    showFreshTile,
    freshTileProject,
    freshTileFading,
    dragging,
    dropTarget,
    selectionState,
    boldApplied,
    boldHover,
    boldPressed,
    cursorX,
    cursorY,
    clicking,
  };
}

/* ---------- Sidebar (faux Notes sidebar) ---------- */

function Sidebar({
  activeProject,
  hoverProject,
  dropTarget,
}: {
  activeProject: string;
  hoverProject: string | null;
  dropTarget: string | null;
}) {
  const all = PROJECTS_BY_ID.all!;
  const projects = PROJECTS.filter((p) => p.id !== "all");
  return (
    <aside className={styles.demoSidebar}>
      <div className={styles.demoSidebarLabel}>Notes</div>

      <SidebarItem
        project={all}
        active={activeProject === "all"}
        hover={hoverProject === "all"}
        dropActive={dropTarget === "all"}
      />

      <div className={styles.demoSidebarLabel}>
        Projects
        <span className={styles.demoSidebarPlus}>+</span>
      </div>

      {projects.map((p) => (
        <SidebarItem
          key={p.id}
          project={p}
          active={activeProject === p.id}
          hover={hoverProject === p.id}
          dropActive={dropTarget === p.id}
        />
      ))}
    </aside>
  );
}

function SidebarItem({
  project,
  active,
  hover,
  dropActive,
}: {
  project: ProjectDef;
  active: boolean;
  hover: boolean;
  dropActive: boolean;
}) {
  const cls = [styles.demoSidebarItem];
  if (active) cls.push(styles.demoSidebarItemActive!);
  if (dropActive) cls.push(styles.demoSidebarDropActive!);
  const isAll = project.id === "all";
  return (
    <div
      className={cls.join(" ")}
      style={
        !active && !dropActive && hover
          ? { background: "rgba(13, 127, 113, 0.1)" }
          : undefined
      }
    >
      {/* "All notes" has no project, so no swatch — invisible
          placeholder keeps the text aligned with the project rows
          below, which do have a color/pattern avatar. */}
      <span
        className={styles.demoSidebarItemAvatar}
        style={
          isAll
            ? { visibility: "hidden" }
            : { background: patternBg(project.color, project.pattern, 18 / 26) }
        }
      />
      <span>{project.name}</span>
      <span className={styles.demoSidebarCount}>{project.count}</span>
    </div>
  );
}

/* ---------- Right pane: list or editor ---------- */

function Pane({
  state,
  sceneIdx,
}: {
  state: DerivedState;
  sceneIdx: number;
}) {
  if (state.openNoteId) {
    return <Editor state={state} sceneIdx={sceneIdx} />;
  }
  return <ListPane state={state} />;
}

function ListPane({ state }: { state: DerivedState }) {
  const filtered = useMemo(() => {
    if (state.activeProject === "all") return TILES;
    return TILES.filter((t) => t.project === state.activeProject);
  }, [state.activeProject]);

  const headerLabel =
    PROJECTS.find((p) => p.id === state.activeProject)?.name ?? "All notes";

  // Mirrors the real app: corner triangle only on the All-notes view,
  // where notes from different projects mix and the marker disambiguates.
  // Inside a specific project, every tile shares its color/pattern —
  // the marker would be redundant.
  const showCorners = state.activeProject === "all";

  // Fresh tile shows up where its current project ends up — in the
  // All-notes view always, and in its specific project view (Inbox
  // before the drag, Reading after).
  const showFresh =
    state.showFreshTile &&
    (state.activeProject === "all" ||
      state.activeProject === state.freshTileProject);

  // While the user is dragging the tile, the ghost overlay represents
  // it visually — keep the original in place but pale so the user
  // tracks where it came from. The real Notes app uses a similar
  // half-opacity treatment under the floating ghost.
  const freshTileExtraClass = state.freshTileFading
    ? styles.demoTileFadeOut
    : "";
  const freshTileExtraStyle = state.dragging
    ? { opacity: 0.4 }
    : undefined;

  const freshProj = PROJECTS_BY_ID[state.freshTileProject]!;

  return (
    <section className={styles.demoPane}>
      <header className={styles.demoPaneHeader}>
        <span className={styles.demoPaneTitle}>{headerLabel}</span>
        <button
          type="button"
          className={
            state.pressNew
              ? `${styles.demoPaneAction} ${styles.demoPaneActionPress}`
              : styles.demoPaneAction
          }
          tabIndex={-1}
        >
          + New
        </button>
      </header>
      <div className={styles.demoPaneBody}>
        <div className={styles.demoGrid}>
          {showFresh ? (
            <article
              key="fresh"
              className={`${styles.demoTile} ${styles.demoTileEnter} ${freshTileExtraClass}`}
              style={freshTileExtraStyle}
            >
              {showCorners ? (
                <span
                  className={styles.demoTileCorner}
                  style={{
                    background: patternBg(
                      freshProj.color,
                      freshProj.pattern,
                      22 / 26,
                    ),
                  }}
                  aria-hidden
                />
              ) : null}
              <h3 className={styles.demoTileTitle}>
                {state.pendingTitle ?? ""}
                {state.pendingTitle !== null &&
                state.pendingTitle.length < NEW_TITLE.length ? (
                  <span className={styles.demoTileCaret} aria-hidden />
                ) : null}
              </h3>
              <p className={styles.demoTileSnippet}>
                Empty note — start typing.
              </p>
            </article>
          ) : null}
          {filtered.map((t) => {
            const proj = PROJECTS_BY_ID[t.project];
            return (
              <article key={t.id} className={styles.demoTile}>
                {showCorners && proj ? (
                  <span
                    className={styles.demoTileCorner}
                    style={{
                      background: patternBg(
                        proj.color,
                        proj.pattern,
                        22 / 26,
                      ),
                    }}
                    aria-hidden
                  />
                ) : null}
                <h3 className={styles.demoTileTitle}>{t.title}</h3>
                <p className={styles.demoTileSnippet}>{t.snippet}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Editor({
  state,
  sceneIdx,
}: {
  state: DerivedState;
  sceneIdx: number;
}) {
  const note = TILES.find((t) => t.id === state.openNoteId);
  if (!note) return null;
  return (
    <section className={styles.demoPane}>
      <header className={styles.demoPaneHeader}>
        <button
          type="button"
          className={styles.demoPaneBack}
          tabIndex={-1}
          style={
            state.pressBack
              ? { filter: "brightness(0.85)", transform: "translateY(1px)" }
              : undefined
          }
        >
          ←
        </button>
        <span className={styles.demoPaneTitle}>{note.title}</span>
      </header>
      <div className={styles.demoPaneBody}>
        <div className={styles.demoEditor}>
          {/* Toolbar — mirrors the real RichEditor: B / I, then a
              bullet + checklist group, then undo/redo. Icons are
              the same SVG glyphs the app uses (ported inline since
              the website has zero workspace deps). Only the B button
              animates state from the demo machine; the others are
              static so the section reads as "the rest of the toolbar
              the user could reach for." */}
          <div className={styles.demoToolbar}>
            <BoldButton state={state} />
            <span className={styles.demoToolbarBtn} aria-hidden>
              <ItalicIcon />
            </span>
            <span className={styles.demoToolbarSep} aria-hidden />
            <span className={styles.demoToolbarBtn} aria-hidden>
              <BulletIcon />
            </span>
            <span className={styles.demoToolbarBtn} aria-hidden>
              <ChecklistIcon />
            </span>
            <span className={styles.demoToolbarSep} aria-hidden />
            <span className={styles.demoToolbarBtn} aria-hidden>
              <UndoIcon />
            </span>
            <span className={styles.demoToolbarBtn} aria-hidden>
              <RedoIcon />
            </span>
          </div>

          <p className={styles.demoEditorBody}>
            <FormattedBody state={state} sceneIdx={sceneIdx} note={note} />
          </p>
        </div>
      </div>
    </section>
  );
}

function BoldButton({ state }: { state: DerivedState }) {
  const cls = [styles.demoToolbarBtn];
  if (state.boldApplied) cls.push(styles.demoToolbarBtnActive!);
  else if (state.boldHover) cls.push(styles.demoToolbarBtnHover!);
  if (state.boldPressed) cls.push(styles.demoToolbarBtnPress!);
  return (
    <span className={cls.join(" ")} aria-hidden>
      B
    </span>
  );
}

/**
 * Renders the editor body in one of four modes, in order of priority:
 *   1. While `bodyTyping`, render the slice + a blinking caret.
 *   2. With selection animating, wrap `BODY_PHRASE` in a span whose
 *      keyframe grows its highlight background from 0 → 100% width.
 *   3. With selection settled, wrap the phrase in a solid-highlight span.
 *   4. With bold applied (selection already cleared), wrap the phrase
 *      in `<strong>` and drop the highlight.
 *
 * The `key` on the selection span is the sceneIdx so the keyframe
 * restarts cleanly each time the scene that owns it re-enters the
 * loop.
 */
function FormattedBody({
  state,
  sceneIdx,
  note,
}: {
  state: DerivedState;
  sceneIdx: number;
  note: TileDef;
}) {
  if (state.bodyTyping) {
    return (
      <>
        {state.bodyText}
        <span className={styles.demoTileCaret} aria-hidden />
      </>
    );
  }
  if (state.bodyText.length === 0) {
    // First render of the editor — no body typed yet. Fall back to
    // the tile's snippet so the editor isn't empty during the
    // click-tile beat right before typing starts.
    return <>{note.snippet}</>;
  }

  const phraseEl = state.boldApplied ? (
    <strong>{BODY_PHRASE}</strong>
  ) : (
    BODY_PHRASE
  );

  if (state.selectionState === "animating") {
    return (
      <>
        {BODY_PRE}
        <span
          key={`sel-${sceneIdx}`}
          className={styles.demoSelectionGrow}
        >
          {phraseEl}
        </span>
        {BODY_POST}
      </>
    );
  }
  if (state.selectionState === "full") {
    return (
      <>
        {BODY_PRE}
        <span className={styles.demoSelectionFull}>{phraseEl}</span>
        {BODY_POST}
      </>
    );
  }

  return (
    <>
      {BODY_PRE}
      {phraseEl}
      {BODY_POST}
    </>
  );
}

/* ---------- App icon ----------
   Direct inline copy of apps/notes/src/icon.svg — the same paper card
   with a fountain pen mid-stroke. Kept in sync with the app icon by
   hand; the website intentionally doesn't pull from the workspace. */
function NotesAppIcon() {
  return (
    <svg
      viewBox="0 0 64 64"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <rect x="14" y="14" width="42" height="44" fill="rgba(23,23,23,0.5)" />
      <rect
        x="8"
        y="8"
        width="42"
        height="44"
        fill="#fff8e5"
        stroke="#171717"
        strokeWidth="2.5"
      />
      <line x1="14" y1="20" x2="44" y2="20" stroke="#171717" strokeWidth="1.2" opacity="0.5" />
      <line x1="14" y1="28" x2="44" y2="28" stroke="#171717" strokeWidth="1.2" opacity="0.5" />
      <line x1="14" y1="36" x2="38" y2="36" stroke="#171717" strokeWidth="1.2" opacity="0.5" />
      <g transform="translate(34 30) rotate(40)">
        <rect x="0" y="-4" width="24" height="8" fill="#0d7f71" stroke="#171717" strokeWidth="2" />
        <rect x="-4" y="-5" width="4" height="10" fill="#e6b94a" stroke="#171717" strokeWidth="2" />
        <path
          d="M-12 0 L-4 -5 L-4 5 Z"
          fill="#171717"
          stroke="#171717"
          strokeWidth="1.5"
          strokeLinejoin="miter"
        />
        <line x1="-10" y1="0" x2="-5" y2="0" stroke="#fff8e5" strokeWidth="0.8" />
      </g>
    </svg>
  );
}

/* ---------- Toolbar icons ----------
   Direct ports of the RichEditor's glyphs (apps/notes/src/RichEditor
   .tsx). Drawn with currentColor so they inherit the button's ink/
   cream styling automatically. */

function ItalicIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      aria-hidden
    >
      <line x1={6} y1={3} x2={12} y2={3} />
      <line x1={4} y1={13} x2={10} y2={13} />
      <line x1={9.5} y1={3} x2={6.5} y2={13} strokeWidth={1.8} />
    </svg>
  );
}

function BulletIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx={3} cy={4} r={1} fill="currentColor" stroke="none" />
      <circle cx={3} cy={8} r={1} fill="currentColor" stroke="none" />
      <circle cx={3} cy={12} r={1} fill="currentColor" stroke="none" />
      <line x1={6.5} y1={4} x2={14} y2={4} />
      <line x1={6.5} y1={8} x2={14} y2={8} />
      <line x1={6.5} y1={12} x2={14} y2={12} />
    </svg>
  );
}

function ChecklistIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="miter"
      aria-hidden
    >
      <rect x={2.5} y={2.5} width={11} height={11} />
      <path
        d="M5 8 L7.3 10.5 L11.3 5.5"
        strokeWidth={1.7}
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 8 a 6 6 0 1 0 2 -5.1 L2 5.3" />
      <polyline points="2,2 2,5.3 5.3,5.3" />
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 8 a 6 6 0 1 1 -2 -5.1 L14 5.3" />
      <polyline points="14,2 14,5.3 10.7,5.3" />
    </svg>
  );
}

/* ---------- Drag ghost ---------- */

/**
 * Floating mini-tile that the cursor pulls along during the
 * pickup → drag → drop sequence. Anchored 12% right and 4% down
 * from the cursor tip — close enough that the two move as one,
 * offset enough that the user can still see the cursor.
 */
function Ghost({
  x,
  y,
  title,
  project,
}: {
  x: number;
  y: number;
  title: string;
  project: "inbox" | "reading";
}) {
  const proj = PROJECTS_BY_ID[project]!;
  return (
    <div
      className={`${styles.demoGhost} ${styles.demoGhostVisible}`}
      style={{ left: `calc(${x}% + 8px)`, top: `calc(${y}% - 4px)` }}
    >
      <span
        className={styles.demoTileCorner}
        style={{
          background: patternBg(proj.color, proj.pattern, 22 / 26),
        }}
        aria-hidden
      />
      <span style={{ position: "relative" }}>{title}</span>
    </div>
  );
}

/* ---------- Cursor ---------- */

function Cursor({
  x,
  y,
  clicking,
}: {
  x: number;
  y: number;
  clicking: boolean;
}) {
  return (
    <div
      className={
        clicking
          ? `${styles.demoCursor} ${styles.demoCursorClick}`
          : styles.demoCursor
      }
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3 2 L3 18 L7.5 13.5 L10.5 19.5 L13 18.2 L10 12.5 L16 12 Z"
          fill="#fff8e5"
          stroke="#171717"
          strokeWidth="1.6"
          strokeLinejoin="miter"
        />
      </svg>
      <span className={styles.demoCursorRipple} aria-hidden />
    </div>
  );
}

/* ============================================================ */
/* PROJECT SHOWCASE — six example color × pattern projects       */
/* ============================================================ */

interface ShowcaseProject {
  name: string;
  colorLabel: string;
  colorHex: string;
  pattern: PatternKey;
}

// Six examples, one per pattern (solid / dots / lines / cross / wave
// / grid). Color picks vary so the row reads as variety, not a
// gradient. Names are evocative rather than the demo's clinical
// Inbox/Reading/Studio set — meant to suggest "things people
// actually keep notes about."
const SHOWCASE_PROJECTS: ShowcaseProject[] = [
  { name: "Reading",  colorLabel: "Teal",       colorHex: "#98d8cf", pattern: "lines" },
  { name: "Cooking",  colorLabel: "Yellow",     colorHex: "#ffe87a", pattern: "dots" },
  { name: "Garden",   colorLabel: "Green",      colorHex: "#b8e09c", pattern: "wave" },
  { name: "Travel",   colorLabel: "Mustard",    colorHex: "#ffce5b", pattern: "grid" },
  { name: "Studio",   colorLabel: "Lavender",   colorHex: "#c8b8e8", pattern: "cross" },
  { name: "Letters",  colorLabel: "Vermillion", colorHex: "#f9a89a", pattern: "solid" },
];

function ProjectShowcase() {
  return (
    <section
      className={styles.showcaseSection}
      aria-labelledby="showcase-title"
    >
      <div className={`atlasContainer ${styles.showcaseInner}`}>
        <header className={styles.sectionHeader}>
          <span className="atlasEyebrow">Color × pattern</span>
          <h2 id="showcase-title" className={styles.sectionTitle}>
            Spot a project{" "}
            <span className={styles.showcaseTitleAccent}>
              at a glance.
            </span>
          </h2>
          <p className={styles.sectionLede}>
            Each project picks one of seven colors and one of six
            patterns. The pairing shows up in the sidebar avatar and
            on every tile's folded corner — so your eye finds the
            right project before your head reads the name.
          </p>
        </header>

        <div className={styles.showcaseGrid}>
          {SHOWCASE_PROJECTS.map((p) => (
            <article key={p.name} className={styles.showcase}>
              <div
                className={styles.showcaseAvatar}
                style={{
                  background: patternBg(p.colorHex, p.pattern, 1.8),
                }}
                aria-hidden
              />
              <h3 className={styles.showcaseName}>{p.name}</h3>
              <p className={styles.showcaseSub}>
                {p.colorLabel} × {p.pattern}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================ */
/* FEATURES                                                      */
/* ============================================================ */

const FEATURES = [
  {
    stamp: "Rich text",
    name: "Rich text without the fuss",
    body: "Bold, italics, headings, lists. No floating toolbar, no markdown to remember. Just type and format what matters.",
  },
  {
    stamp: "Projects",
    name: "Projects, not folders",
    body: "Each project picks a color and a pattern. Drag a note from the list onto a project to reassign — no nested hierarchy to maintain.",
  },
  {
    stamp: "Drag & drop",
    name: "Drag to reorganize",
    body: "Move a note between projects by dragging its tile to the sidebar. Multi-window works the same way — drop into the other window's sidebar.",
  },
  {
    stamp: "Multi-window",
    name: "Open as many as you want",
    body: "Each Notes window keeps its own view. Open one project in one window, another in a second, side by side via Layouts.",
  },
  {
    stamp: "Inbox",
    name: "Inbox by default",
    body: "Every new note lands in Inbox until you decide where it belongs. Triage, don't pre-organize.",
  },
  {
    stamp: "Offline",
    name: "Offline. On your machine.",
    body: "Local IndexedDB, no sync, no telemetry. Your writing is yours — walk away with the .txt export whenever you like.",
  },
];

function Features() {
  return (
    <section
      className={styles.featureSection}
      aria-labelledby="notes-features-title"
    >
      <div className={`atlasContainer ${styles.featureInner}`}>
        <header className={styles.sectionHeader}>
          <span className="atlasEyebrow">What it does</span>
          <h2 id="notes-features-title" className={styles.sectionTitle}>
            A small set of things,{" "}
            <span className={styles.demoTitleAccent}>
              done carefully.
            </span>
          </h2>
        </header>

        <div className={styles.featureGrid}>
          {FEATURES.map((f) => (
            <article key={f.name} className={styles.feature}>
              <span className={styles.featureStamp}>{f.stamp}</span>
              <h3 className={styles.featureName}>{f.name}</h3>
              <p className={styles.featureBody}>{f.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================ */
/* CTA                                                           */
/* ============================================================ */

function Cta() {
  return (
    <section
      className={styles.ctaSection}
      aria-labelledby="notes-cta-title"
    >
      <div className={`atlasContainer ${styles.ctaInner}`}>
        <span className={styles.ctaEyebrow}>Free with Atlas Desk</span>
        <h2 id="notes-cta-title" className={styles.ctaTitle}>
          Write the way you{" "}
          <span className={styles.ctaTitleAccent}>think.</span>
        </h2>
        <p className={styles.ctaLede}>
          Notes lives in the Atlas Desk app registry — install it the
          moment you want it, free, alongside Stickies, Music, Mail,
          and the rest. Pick only the apps you'll use; no add-ons, no
          upsell, no account to make.
        </p>
        <div className={styles.ctaActions}>
          <a className={styles.ctaPrimary} href="/#download">
            Download Atlas Desk <span aria-hidden>→</span>
          </a>
          <a className={styles.ctaSecondary} href="/#apps">
            See every app →
          </a>
        </div>
      </div>
    </section>
  );
}
